import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

function safeV2Redirect(raw: string | null): string | null {
  if (typeof raw !== "string") return null;
  if (!raw.startsWith("/v2/")) return null;
  if (raw.startsWith("//")) return null;
  if (raw.includes("\\")) return null;
  if (raw.includes("http")) return null;
  if (raw.includes(":")) return null;
  return raw;
}
import { Helmet } from "react-helmet-async";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

const loginSchema = z.object({
  email: z.string().trim().email("Inserisci un'email valida"),
  password: z.string().min(1, "Inserisci la password"),
});

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn } = useAuth();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    setIsSubmitting(true);
    const { error } = await signIn(values.email, values.password);
    setIsSubmitting(false);

    if (!error) {
      const requested = searchParams.get("redirect");
      const safe = safeV2Redirect(requested);
      if (safe) {
        navigate(safe);
      } else if (requested && requested.startsWith("/v2/")) {
        // Param looked V2 but failed strict validation → safe fallback inside V2 funnel
        navigate("/v2/pricing");
      } else {
        navigate("/v2/sprint");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 font-body antialiased flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Accedi | Progetto Startup</title>
        <meta name="description" content="Accedi al tuo account Progetto Startup" />
      </Helmet>

      <div className="w-full max-w-md">
        <div className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-2xl p-8">
          <div className="text-center mb-8">
            <a href="/" className="inline-block mb-6 font-display font-semibold tracking-tight text-xl text-slate-100">
              Progetto Startup
            </a>
            <h1 className="text-2xl font-bold text-slate-100 mb-2">Bentornato!</h1>
            <p className="text-slate-400 text-sm">Accedi al tuo account</p>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm text-slate-400">Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="nome@esempio.com"
                        {...field}
                        className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#FF6B47] focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                    </FormControl>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm text-slate-400">Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          {...field}
                          className="bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500 focus:border-[#FF6B47] focus-visible:ring-0 focus-visible:ring-offset-0 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                          aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-400" />
                  </FormItem>
                )}
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-xl bg-gradient-to-r from-[#FF6B47] to-[#FF8A6B] text-white font-semibold py-2.5 text-sm transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 mt-2"
              >
                {isSubmitting ? "Accesso..." : "Accedi"}
              </button>
            </form>
          </Form>

          <div className="mt-6 text-center text-sm text-slate-500">
            Non hai un account?{" "}
            <a href="/signup" className="text-[#FF6B47] hover:text-[#FF8A6B] font-medium transition-colors">
              Registrati
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;